import { useState } from 'react';
import {
  Shield,
  AlertTriangle,
  XCircle,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import type { ADACheckSeverity } from '../../../types/preview';
import { usePreviewStore } from '../../../stores/previewStore';

export default function ADACompliancePanel() {
  const { adaCheckResults, showADAPanel, toggleADAPanel } = usePreviewStore();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const errorCount = adaCheckResults.filter((r) => r.severity === 'error').length;
  const warningCount = adaCheckResults.filter((r) => r.severity === 'warning').length;
  const infoCount = adaCheckResults.filter((r) => r.severity === 'info').length;

  const toggleItem = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getSeverityIcon = (severity: ADACheckSeverity) => {
    switch (severity) {
      case 'error':
        return <XCircle className="w-3.5 h-3.5 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
      case 'info':
        return <Info className="w-3.5 h-3.5 text-blue-400" />;
    }
  };

  const getSeverityBg = (severity: ADACheckSeverity) => {
    switch (severity) {
      case 'error':
        return 'bg-red-500/10 border-red-500/20';
      case 'warning':
        return 'bg-amber-500/10 border-amber-500/20';
      case 'info':
        return 'bg-blue-500/10 border-blue-500/20';
    }
  };

  return (
    <div className="border-t border-white/[0.06]">
      <button
        onClick={toggleADAPanel}
        className="w-full flex items-center gap-2 px-4 py-2 hover:bg-white/[0.04] transition-colors"
      >
        <Shield className="w-4 h-4 text-purple-400" />
        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
          ADA Compliance
        </span>

        <div className="flex items-center gap-2 ml-auto">
          {adaCheckResults.length === 0 ? (
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400">All clear</span>
            </div>
          ) : (
            <>
              {errorCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">
                  {errorCount} error{errorCount !== 1 ? 's' : ''}
                </span>
              )}
              {warningCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  {warningCount} warning{warningCount !== 1 ? 's' : ''}
                </span>
              )}
              {infoCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  {infoCount} info
                </span>
              )}
            </>
          )}

          {showADAPanel ? (
            <ChevronDown className="w-3.5 h-3.5 text-white/40" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-white/40" />
          )}
        </div>
      </button>

      {showADAPanel && (
        <div className="px-3 pb-3 max-h-64 overflow-y-auto sidebar-scroll">
          {adaCheckResults.length === 0 ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-emerald-400 font-medium">No issues found</p>
              <p className="text-xs text-white/40 mt-1">
                Your design passes all ADA compliance checks
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {adaCheckResults.map((result) => (
                <div
                  key={result.id}
                  className={`rounded-lg border ${getSeverityBg(result.severity)}`}
                >
                  <button
                    onClick={() => toggleItem(result.id)}
                    className="w-full flex items-start gap-2 p-2 text-left"
                  >
                    <div className="mt-0.5">{getSeverityIcon(result.severity)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white/80">{result.message}</p>
                      {result.elementName && (
                        <p className="text-[10px] text-white/40 mt-0.5">
                          Element: {result.elementName}
                        </p>
                      )}
                    </div>
                    {expandedItems.has(result.id) ? (
                      <ChevronDown className="w-3 h-3 text-white/30 mt-0.5 shrink-0" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-white/30 mt-0.5 shrink-0" />
                    )}
                  </button>

                  {expandedItems.has(result.id) && (
                    <div className="px-2 pb-2 pt-0 ml-6">
                      <div className="p-2 rounded bg-black/20">
                        <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1">
                          Suggestion
                        </p>
                        <p className="text-xs text-white/70">{result.suggestion}</p>
                      </div>
                      <p className="text-[10px] text-white/30 mt-1.5">
                        Rule: {result.rule}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-white/[0.04]">
            <a
              href="https://www.w3.org/WAI/WCAG21/quickref/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-[10px] text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              WCAG 2.1 Quick Reference
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
